import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Loader2,
  HardDrive,
  Download,
  Trash2,
  RefreshCw,
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Github,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  getBranchBackups,
  createBranchBackup,
  type BranchBackup,
} from '@/api/deployments';
import {
  deleteBackup,
  downloadBackup,
  restoreBackup,
  validateServerStorage,
} from '@/api/project';

interface BackupsTabProps {
  projectId: string;
  branchId: number;
  githubRepo?: string | null;
}

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const lowerStatus = status.toLowerCase();

  if (lowerStatus === 'completed' || lowerStatus === 'success') {
    return (
      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
        <CheckCircle className="h-3 w-3 mr-1" />
        Completed
      </Badge>
    );
  }
  if (lowerStatus === 'running' || lowerStatus === 'in_progress') {
    return (
      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
        Running
      </Badge>
    );
  }
  if (lowerStatus === 'failed' || lowerStatus === 'error') {
    return (
      <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
        <XCircle className="h-3 w-3 mr-1" />
        Failed
      </Badge>
    );
  }
  if (lowerStatus === 'pending') {
    return (
      <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
        <Clock className="h-3 w-3 mr-1" />
        Pending
      </Badge>
    );
  }
  if (lowerStatus === 'deleting') {
    return (
      <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
        <Trash2 className="h-3 w-3 mr-1 animate-pulse" />
        Deleting
      </Badge>
    );
  }
  return <Badge variant="secondary">{status}</Badge>;
}

// Format file size
function formatSize(sizeMb: number | null): string {
  if (!sizeMb) return '—';
  if (sizeMb >= 1024) {
    return `${(sizeMb / 1024).toFixed(1)} GB`;
  }
  return `${sizeMb.toFixed(1)} MB`;
}

// Format date in user's local timezone
function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleString();
}

// Backup row component
function BackupRow({
  backup,
  projectId,
  githubRepo,
  buildServerId,
  onRefresh,
}: {
  backup: BranchBackup;
  projectId: string;
  githubRepo?: string | null;
  buildServerId?: number;
  onRefresh: () => void;
}) {
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  const [downloadState, setDownloadState] = useState<{
    loading: boolean;
    error: string | null;
    backupType: 'zip' | 'separate_files' | null;
    databaseUrl: string | null;
    filestoreUrl: string | null;
  }>({ loading: false, error: null, backupType: null, databaseUrl: null, filestoreUrl: null });

  // Restore dialog state
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [overrideStorageWarning, setOverrideStorageWarning] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isDeletingBackup, setIsDeletingBackup] = useState(false);
  const [storageValidation, setStorageValidation] = useState<{
    loading: boolean;
    error: string | null;
    hasSufficientSpace: boolean | null;
    backupSizeGb: number | null;
    requiredSpaceGb: number | null;
    availableSpaceGb: number | null;
    spaceShortageGb: number | null;
  }>({
    loading: false,
    error: null,
    hasSufficientSpace: null,
    backupSizeGb: null,
    requiredSpaceGb: null,
    availableSpaceGb: null,
    spaceShortageGb: null,
  });

  const isCompleted = backup.status.toLowerCase() === 'completed';
  const isDeleting = backup.status.toLowerCase() === 'deleting';

  const handleOpenDownload = async () => {
    setIsDownloadOpen(true);
    setDownloadState({ loading: true, error: null, backupType: null, databaseUrl: null, filestoreUrl: null });

    try {
      const result = await downloadBackup(projectId, backup.id);

      if (result.success) {
        setDownloadState({
          loading: false,
          error: null,
          backupType: result.backup_type || null,
          databaseUrl: result.database_url || null,
          filestoreUrl: result.filestore_url || null,
        });
      } else {
        setDownloadState({
          loading: false,
          error: result.error || 'Failed to generate download links',
          backupType: null,
          databaseUrl: null,
          filestoreUrl: null,
        });
      }
    } catch (err) {
      setDownloadState({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to generate download links',
        backupType: null,
        databaseUrl: null,
        filestoreUrl: null,
      });
    }
  };

  // Handle opening restore dialog - validate storage
  const handleOpenRestore = async () => {
    setIsRestoreOpen(true);
    setConfirmRestore(false);
    setOverrideStorageWarning(false);

    // Validate storage if we have server ID and backup size
    if (buildServerId && backup.size_mb) {
      setStorageValidation((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const result = await validateServerStorage(buildServerId, backup.size_mb);

        if (result.error) {
          setStorageValidation({
            loading: false,
            error: result.error,
            hasSufficientSpace: null,
            backupSizeGb: null,
            requiredSpaceGb: null,
            availableSpaceGb: null,
            spaceShortageGb: null,
          });
        } else {
          setStorageValidation({
            loading: false,
            error: null,
            hasSufficientSpace: result.has_sufficient_space ?? null,
            backupSizeGb: result.backup_size_gb ?? null,
            requiredSpaceGb: result.required_space_gb ?? null,
            availableSpaceGb: result.available_space_gb ?? null,
            spaceShortageGb: result.space_shortage_gb ?? null,
          });
        }
      } catch {
        setStorageValidation({
          loading: false,
          error: 'Failed to check server storage',
          hasSufficientSpace: null,
          backupSizeGb: null,
          requiredSpaceGb: null,
          availableSpaceGb: null,
          spaceShortageGb: null,
        });
      }
    } else {
      setStorageValidation({
        loading: false,
        error: backup.size_mb ? null : 'Could not determine backup size',
        hasSufficientSpace: null,
        backupSizeGb: null,
        requiredSpaceGb: null,
        availableSpaceGb: null,
        spaceShortageGb: null,
      });
    }
  };

  const canRestore = () => {
    if (!confirmRestore || isRestoring) return false;
    if (storageValidation.hasSufficientSpace === false && !overrideStorageWarning) return false;
    return true;
  };

  const handleRestore = async () => {
    if (!canRestore() || !backup.build) return;
    setIsRestoring(true);
    try {
      const result = await restoreBackup(projectId, backup.id, backup.build.id);
      if (result.success) {
        toast.success('Restore started');
        setIsRestoreOpen(false);
        onRefresh();
      } else {
        toast.error(result.error || 'Failed to restore backup');
      }
    } catch {
      toast.error('Failed to restore backup');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDelete = async () => {
    setIsDeletingBackup(true);
    try {
      const result = await deleteBackup(projectId, backup.id);
      if (result.success) {
        toast.success('Backup deletion started');
        setIsDeleteOpen(false);
        onRefresh();
      } else {
        toast.error(result.error || 'Failed to delete backup');
      }
    } catch {
      toast.error('Failed to delete backup');
    } finally {
      setIsDeletingBackup(false);
    }
  };

  return (
    <>
      <tr className="border-b hover:bg-gray-50">
        {/* Date */}
        <td className="py-2 px-3 text-sm whitespace-normal break-words">{formatDate(backup.created_at)}</td>

        {/* Database */}
        <td className="py-2 px-3 text-sm max-w-[180px] break-words" title={backup.build?.db_name || ''}>
          {backup.build?.db_name || '—'}
        </td>

        {/* Odoo Version */}
        <td className="py-2 px-3">
          {backup.odoo_version ? (
            <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">
              Odoo {backup.odoo_version}
            </Badge>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>

        {/* Commit */}
        <td className="py-2 px-3 break-words">
          {backup.commit_hash && githubRepo ? (
            <a
              href={`https://github.com/${githubRepo}/commit/${backup.commit_hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center"
            >
              <Badge variant="default" className="bg-primary hover:bg-primary/90">
                <Github className="h-3 w-3 mr-1" />#{backup.commit_hash.slice(0, 7)}
              </Badge>
            </a>
          ) : backup.commit_hash ? (
            <span className="text-xs font-mono text-gray-500">#{backup.commit_hash.slice(0, 7)}</span>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>

        {/* Status */}
        <td className="py-2 px-3">
          <StatusBadge status={backup.status} />
        </td>

        {/* Size */}
        <td className="py-2 px-3 text-sm text-gray-600">{formatSize(backup.size_mb)}</td>

        {/* Comments */}
        <td className="py-2 px-3 text-sm text-gray-500 max-w-[220px] break-words" title={backup.comments || ''}>
          {backup.comments || '—'}
        </td>

        {/* Actions */}
        <td className="py-2 px-3 text-right">
          <div className="flex items-center justify-end gap-2 flex-wrap">
            {isCompleted && (
              <>
                <Button variant="outline" size="sm" onClick={handleOpenDownload} className="h-7 px-2" title="Download">
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleOpenRestore} className="h-7 px-2" title="Restore">
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeleteOpen(true)}
              disabled={isDeleting}
              className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </td>
      </tr>

      {/* Download Dialog */}
      <Dialog open={isDownloadOpen} onOpenChange={setIsDownloadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download Backup</DialogTitle>
            <DialogDescription>{backup.created_at && `Backup from ${formatDate(backup.created_at)}`}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {downloadState.loading && (
              <div className="flex flex-col items-center py-6">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                <p className="text-sm text-gray-500">Generating download links...</p>
              </div>
            )}

            {downloadState.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{downloadState.error}</AlertDescription>
              </Alert>
            )}

            {!downloadState.loading && !downloadState.error && downloadState.backupType === 'zip' && (
              <div className="space-y-4">
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertDescription>This link is valid for 1 hour.</AlertDescription>
                </Alert>
                <Button asChild className="w-full" size="lg">
                  <a href={downloadState.databaseUrl || '#'} target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download Backup
                  </a>
                </Button>
              </div>
            )}

            {!downloadState.loading && !downloadState.error && downloadState.backupType === 'separate_files' && (
              <div className="space-y-4">
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertDescription>These links are valid for 1 hour.</AlertDescription>
                </Alert>
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800">
                    This backup consists of separate database and filestore files. Download both for a complete backup.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Button asChild className="w-full">
                    <a href={downloadState.databaseUrl || '#'} target="_blank" rel="noopener noreferrer">
                      <HardDrive className="h-4 w-4 mr-2" />
                      Download Database
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="w-full">
                    <a href={downloadState.filestoreUrl || '#'} target="_blank" rel="noopener noreferrer">
                      <Download className="h-4 w-4 mr-2" />
                      Download Filestore
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDownloadOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={isRestoreOpen} onOpenChange={(open) => !isRestoring && setIsRestoreOpen(open)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Restore Backup</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Warning Alert */}
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>WARNING: This is a destructive action!</AlertTitle>
              <AlertDescription>
                Restoring this backup will permanently delete all current data on the deployment and replace it with the
                backup's data.
              </AlertDescription>
            </Alert>

            {/* Backup Information */}
            <div>
              <Label className="text-sm font-medium">Backup Information</Label>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Created:</span>{' '}
                    <span className="font-medium">{formatDate(backup.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Database:</span>{' '}
                    <span className="font-medium">{backup.build?.db_name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Version:</span>{' '}
                    <span className="font-medium">Odoo {backup.odoo_version || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Size:</span>{' '}
                    <span className="font-medium">{formatSize(backup.size_mb)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Storage Validation */}
            {buildServerId && (
              <div>
                <Label className="text-sm font-medium">Storage Requirements</Label>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                  {storageValidation.loading ? (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-gray-500">Checking server storage...</span>
                    </div>
                  ) : storageValidation.error ? (
                    <div className="flex items-center text-yellow-600">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      <span className="text-sm">{storageValidation.error}</span>
                    </div>
                  ) : storageValidation.hasSufficientSpace !== null ? (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Backup Size:</span>{' '}
                        <span className="font-medium">{storageValidation.backupSizeGb} GB</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Required:</span>{' '}
                        <span className="font-medium">{storageValidation.requiredSpaceGb} GB</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Available:</span>{' '}
                        <span className="font-medium">{storageValidation.availableSpaceGb} GB</span>
                      </div>
                      <div>
                        {storageValidation.hasSufficientSpace ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Sufficient Space
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Insufficient Space
                          </Badge>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Storage validation unavailable</span>
                  )}
                </div>
              </div>
            )}

            {/* Insufficient Storage Warning */}
            {storageValidation.hasSufficientSpace === false && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Insufficient Storage Space!</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    The server needs an additional {storageValidation.spaceShortageGb} GB of free space to restore this
                    backup.
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="checkbox"
                      id="overrideStorage"
                      checked={overrideStorageWarning}
                      onChange={(e) => setOverrideStorageWarning(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor="overrideStorage" className="text-sm font-medium">
                      I understand the risks and want to proceed anyway
                    </label>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Confirmation Checkbox */}
            <div className="flex items-center space-x-2 pt-2 border-t">
              <input
                type="checkbox"
                id="confirmRestore"
                checked={confirmRestore}
                onChange={(e) => setConfirmRestore(e.target.checked)}
                disabled={isRestoring}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="confirmRestore" className="text-sm">
                I understand that all current data will be permanently deleted and replaced with the backup data.
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRestoreOpen(false)} disabled={isRestoring}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRestore} disabled={!canRestore()}>
              {isRestoring ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                'Restore Backup'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={(open) => !isDeletingBackup && setIsDeleteOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Backup</DialogTitle>
            <DialogDescription>Are you sure you want to delete this backup? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} disabled={isDeletingBackup}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeletingBackup}>
              {isDeletingBackup ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Create Backup Modal
function CreateBackupModal({
  open,
  onOpenChange,
  projectId,
  branchId,
  buildInfo,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  branchId: number;
  buildInfo: { fqdn: string; db_name: string; odoo_version: string | null } | null;
  onSuccess: () => void;
}) {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const result = await createBranchBackup(projectId, branchId, comment || undefined);
      if (result.success) {
        toast.success('Backup started');
        onOpenChange(false);
        setComment('');
        onSuccess();
      } else {
        toast.error(result.error || 'Failed to create backup');
      }
    } catch {
      toast.error('Failed to create backup');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Backup</DialogTitle>
          <DialogDescription>Create a backup of this branch's deployment.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {buildInfo ? (
            <div>
              <Label>Deployment</Label>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                <div className="font-medium">{buildInfo.fqdn}</div>
                <div className="text-sm text-gray-500">
                  Database: {buildInfo.db_name}
                  {buildInfo.odoo_version && ` • Odoo ${buildInfo.odoo_version}`}
                </div>
              </div>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>No active deployment found for this branch.</AlertDescription>
            </Alert>
          )}
          <div>
            <Label>Comment (optional)</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note about this backup..."
              className="mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!buildInfo || isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Backup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BackupsTab({ projectId, branchId, githubRepo }: BackupsTabProps) {
  const [page, setPage] = useState(1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['branch-backups', projectId, branchId, page],
    queryFn: () => getBranchBackups(projectId, branchId, page),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>Failed to load backups. Please try refreshing the page.</AlertDescription>
      </Alert>
    );
  }

  // Check if API returned an error
  if (data && data.error) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{data.error}</AlertDescription>
      </Alert>
    );
  }

  // Check if backup storage is enabled (explicitly check for false, not undefined)
  if (data && data.backup_storage_enabled === false) {
    return (
      <Alert className="mt-4 bg-yellow-50 border-yellow-200">
        <AlertCircle className="h-4 w-4 text-yellow-600" />
        <AlertTitle className="text-yellow-800">Backup Storage Add-on Required</AlertTitle>
        <AlertDescription className="text-yellow-700">
          <p className="mb-2">
            To use backups, you need to add the Backup Storage add-on to your subscription. Backups are billed at{' '}
            <strong>$0.02 per GB per month</strong>.
          </p>
          <Button variant="outline" className="border-yellow-500 text-yellow-700 hover:bg-yellow-100" asChild>
            <a href={`/project/${projectId}/settings#billing`}>
              <Plus className="h-4 w-4 mr-2" />
              Add Backup Storage
            </a>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const backups = data?.backups || [];
  const pagination = data?.pagination;
  const buildInfo = data?.build;

  return (
    <div className="mt-4 space-y-4 w-full max-w-7xl mx-auto break-words min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Backups
          </h2>
          <p className="text-sm text-gray-500">Manage database backups for this branch</p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)} disabled={!buildInfo}>
          <Plus className="h-4 w-4 mr-2" />
          Create Backup
        </Button>
      </div>

      {/* Backups Table */}
      <Card className="min-w-0">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">All Backups</CardTitle>
            <CardDescription>{pagination?.total || 0} total</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {backups.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <HardDrive className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">No backups yet</p>
              <p className="text-sm">Create your first backup to get started.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50 border-y">
                    <tr>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Database</th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Odoo</th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Commit</th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                      <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.map((backup) => (
                      <BackupRow
                        key={backup.id}
                        backup={backup}
                        projectId={projectId}
                        githubRepo={githubRepo}
                        buildServerId={buildInfo?.server_id}
                        onRefresh={refetch}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-2 py-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!pagination.has_prev}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-500">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={!pagination.has_next}>
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Backup Modal */}
      <CreateBackupModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        projectId={projectId}
        branchId={branchId}
        buildInfo={buildInfo ? { fqdn: buildInfo.fqdn, db_name: buildInfo.db_name, odoo_version: buildInfo.odoo_version } : null}
        onSuccess={refetch}
      />
    </div>
  );
}
